import { PublishedLegend } from '@/features/jobs/legends/PublishedLegend'
import type { AlertRetryWrapperFactory } from '@/features/shell'
import { truncate } from 'lodash-es'
import { action, computed, observable } from 'mobx'
import {
  combineLatest,
  type Observable,
  of,
  Subject,
  firstValueFrom,
} from 'rxjs'
import { catchError, switchMap, takeUntil } from 'rxjs/operators'
import { fromStream, toStream } from '@/utils/mobx-util'
import {
  filterNonNil,
  fromAbortSignal,
  shareLatest,
  unpair,
  whileActive,
  withAbortSignal,
} from '@/utils/rx-util'
import { Member } from '@/features/iam/members/Member'
import { type ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import {
  type RequestedProviderChanged,
  RequestedProviderSelection,
} from './DraftPersister'
import { type DraftStateSlice, type PrepareContext } from './DraftJobViewModel'
import {
  type OutlineItemDisplayVisual,
  type StepAccessor,
  type StepFactory,
  type StepViewModel,
} from './OutlineViewModel'
import { type ProviderStore } from '@/features/jobs/ProviderStore'

const TITLE = 'Would you like to reconnect with a previous Pro?'
const DESCRIPTION = `
Please keep in mind that if the Pro selected does not have
capacity to take on more jobs, your job may be transferred
to another qualified Pro. Choosing this option will manually
route you to the selected Pro. Please do not expect an
immediate response from them upon submission. The Pro will
be notified the job has been routed and they will reach out
to you next time they log in.
`
  .trim()
  .replace(/\n/g, ' ')

/**
 * Provider Selection VM.
 */
export class PreviousProviderSelectionViewModel
  implements ActivationLifecycle, DraftStateSlice
{
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * Changes subject.
   * @private
   */
  private readonly _changesSubject = new Subject<RequestedProviderChanged>()

  /**
   * Changes as observable.
   */
  readonly changes$ = this._changesSubject.asObservable()

  /**
   * Step VM.
   */
  readonly step: StepViewModel

  /**
   * The selected provider, if any.
   * `null` = no selection
   * `RequestedProviderSelection.firstAvailable` = first available
   */
  @observable accessor selected: RequestedProviderSelection | null = null

  /**
   * Stream for whether the step is valid.
   */
  readonly isValid$ = toStream(() => this.isValid, true)

  /**
   * Stream of available providers. Emits after first query.
   */
  readonly availableProviders$: Observable<Member[]>

  /**
   * Streams as observables.
   * @private
   */
  private readonly _$

  /**
   * Constructor.
   */
  constructor(
    createStep: StepFactory,
    private readonly _providerStore: ProviderStore,
    private readonly _createAlertRetryWrapper: AlertRetryWrapperFactory,
    private readonly _member$: Observable<Member | null>,
    private readonly _legend$: Observable<PublishedLegend | null>,
    private readonly _isDisabled: () => boolean,
    private readonly _canSaveJobAsAdmin: () => boolean,
  ) {
    this.availableProviders$ = combineLatest([
      // We need the selected WS member and the selected Legend
      // to figure out whether we should query for pros and for which WS.
      this._member$,
      this._legend$,
    ]).pipe(
      unpair(),
      // Don't do anything until we have selected both WS and Legend.
      filterNonNil(),
      switchMap((member) =>
        toStream(() => this.isAllowedToSelectProvider, true).pipe(
          switchMap((allowed) =>
            allowed
              ? withAbortSignal((signal) =>
                  this._createAlertRetryWrapper(
                    'Loading previous providers failed',
                    signal,
                  )(() =>
                    this._providerStore.fetchAvailableProvidersForMember(
                      member,
                      signal,
                    ),
                  ),
                ).pipe(
                  // With no providers the step is hidden.
                  catchError(() => of([])),
                )
              : of([]),
          ),
        ),
      ),
      shareLatest(),
      whileActive(this._lifecycle.active$),
    )

    this._$ = {
      member: fromStream(Member.none, this._member$),
      legend: fromStream(PublishedLegend.none, this._legend$),
      availableProviders: fromStream([], this.availableProviders$),
    }

    this.step = createStep({
      stepId: 'provider_selection',
      isCompleted: () => Boolean(this.selected),
      outlineItemDisplay: () => {
        if (!this.selected) {
          return {
            title: TITLE,
            description: DESCRIPTION,
          }
        }

        const value =
          this.selected === RequestedProviderSelection.firstAvailable
            ? 'First Available'
            : this.selected.displayName

        const visual: OutlineItemDisplayVisual =
          this.selected === RequestedProviderSelection.firstAvailable
            ? { type: 'EmptyAvatar' }
            : { type: 'Image', url: this.selected.avatar }

        return {
          value,
          visual,
          title: TITLE,
          description: DESCRIPTION,
          shortValue: truncate(value, { length: 25 }),
        }
      },
    })
  }

  /**
   * Whether the step is valid.
   */
  @computed
  get isValid() {
    return !this.isProviderSelectionRelevant || Boolean(this.selected)
  }

  /**
   * Whether the editor is disabled.
   */
  @computed
  get disabled() {
    return this._isDisabled()
  }

  /**
   * Available providers.
   */
  @computed
  get availableProviders() {
    return this._$.availableProviders()
  }

  /**
   * Whether selecting a provider is allowed.
   */
  @computed
  get isAllowedToSelectProvider() {
    return Boolean(this._$.legend()?.allowProSelection)
  }

  /**
   * Whether provider selection is relevant.
   */
  @computed
  get isProviderSelectionRelevant() {
    return (
      !this._canSaveJobAsAdmin() &&
      this.isAllowedToSelectProvider &&
      this.availableProviders.length > 0
    )
  }

  /**
   * The step accessors.
   */
  @computed
  get stepAccessors(): StepAccessor[] {
    return this.isProviderSelectionRelevant ? [this] : []
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Prepares the VM.
   *
   * @param context
   * @param signal
   */
  async prepare(context: PrepareContext, signal: AbortSignal): Promise<void> {
    if (context.type !== 'EditDraft') {
      return
    }

    await this.waitForProvidersToLoad(signal)
    signal.throwIfAborted()

    if (!context.job.requestedProId) {
      return
    }

    const pro = this.availableProviders.find(
      (p) => p.user.id === context.job.requestedProId,
    )

    if (pro) {
      // The pro is still available, so restore that state.
      this.setRequestedPro(pro)
    } else {
      // The pro is no longer in the list, so emit a change that
      // resets to no selection.
      this.select(null)
    }
  }

  /**
   * Selects the requested provider and emits a change.
   *
   * @param requestedProvider
   */
  @action.bound
  select(requestedProvider: RequestedProviderSelection | null | undefined) {
    requestedProvider ??= null

    // Only emit the change if we actually made a change.
    if (this.selected !== requestedProvider) {
      this.setRequestedPro(requestedProvider)
      this._changesSubject.next({
        requestedProvider,
        type: 'RequestedProviderChanged',
      })
    }
  }

  /**
   * Selects the first available provider.
   */
  @action.bound
  selectFirstAvailable() {
    this.select(RequestedProviderSelection.firstAvailable)
  }

  /**
   * Sets the requested Pro.
   *
   * @param requestedProvider
   */
  @action.bound
  setRequestedPro(requestedProvider: RequestedProviderSelection | null) {
    this.selected = requestedProvider
  }

  /**
   * Waits for available providers to emit once.
   *
   * @param signal
   */
  async waitForProvidersToLoad(signal: AbortSignal) {
    await firstValueFrom(
      this.availableProviders$.pipe(takeUntil(fromAbortSignal(signal))),
      {
        defaultValue: undefined,
      },
    )
  }
}

/**
 * Factory for creating a {@link PreviousProviderSelectionViewModel}.
 */
export type PreviousProviderSelectionViewModelFactory = ReturnType<
  typeof createPreviousProviderSelectionViewModelFactory
>

/** @see PreviousProviderSelectionViewModelFactory */
export function createPreviousProviderSelectionViewModelFactory(
  providerStore: ProviderStore,
  createAlertRetryWrapper: AlertRetryWrapperFactory,
) {
  return (
    createStep: StepFactory,
    member$: Observable<Member | null>,
    legend$: Observable<PublishedLegend | null>,
    isDisabled: () => boolean,
    canSaveJobAsAdmin: () => boolean,
  ) =>
    new PreviousProviderSelectionViewModel(
      createStep,
      providerStore,
      createAlertRetryWrapper,
      member$,
      legend$,
      isDisabled,
      canSaveJobAsAdmin,
    )
}
